import type { BoxProps, ImageProps } from '@hover/blueprint';
import {
  Body,
  Box,
  Image,
  Logo,
  Divider as SystemDivider,
} from '@hover/blueprint';
import React from 'react';

import { FeatureFlag } from 'src/shared/constants';
import { useTreatment } from 'src/shared/hooks';
import { isEnabled } from 'src/shared/telemetry/featureFlag';

interface CobrandLogoProps extends BoxProps {
  cobranding: {
    logoUrl?: string;
    logoUrlInvert?: string;
    displayName?: string;
  };
  hideHoverMark?: boolean;
  colorMode?: 'light' | 'dark';
  height?: number;
  variant?: 'text' | 'transparent' | 'pill';
  imageProps?: Partial<ImageProps>;
}

export const CobrandedLogo = ({
  cobranding,
  hideHoverMark = false,
  colorMode = 'light',
  height = 200,
  variant = 'transparent',
  imageProps,
  ...props
}: CobrandLogoProps) => {
  const showDivider = cobranding?.logoUrl && !hideHoverMark;
  const showCobranding = cobranding?.logoUrl;

  const useNewLogo = isEnabled(useTreatment(FeatureFlag.Web.BAUHAUS_LOGO));

  return (
    <Box
      alignItems="center"
      data-testid="cobrand-logo"
      justifyContent="flex-start"
      zIndex={1}
      {...props}
    >
      {!hideHoverMark && (
        <Logo
          colorMode={colorMode}
          height={useNewLogo ? 100 : height}
          variant={useNewLogo ? undefined : 'symbol'}
        />
      )}

      {showDivider && (
        <SystemDivider
          borderBottomWidth={0}
          borderColor="neutral.200"
          borderLeftWidth="1px"
          height="22px"
          margin="0"
          mx={200}
          width={0}
        />
      )}

      {showCobranding && variant === 'pill' && (
        <Box
          backgroundColor="neutral.100"
          borderRadius={200}
          height={height}
          justifyContent="center"
          ml={100}
          objectFit="cover"
          p={100}
        >
          <Image
            alt="cobrand logo"
            display="block"
            height="100%"
            src={cobranding?.logoUrl}
            width="auto"
            {...imageProps}
          />
        </Box>
      )}

      {showCobranding && variant === 'transparent' && (
        <Image
          alt="cobrand logo"
          height={height}
          src={
            colorMode === 'light'
              ? cobranding?.logoUrl
              : cobranding?.logoUrlInvert
          }
          {...imageProps}
        />
      )}

      {showCobranding && variant === 'text' && (
        <Body
          color={colorMode === 'dark' ? 'neutral.100' : 'neutral.900'}
          margin="0 !important"
        >
          {cobranding?.displayName}
        </Body>
      )}
    </Box>
  );
};
